import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { deleteRequest, getRequest, postRequest } from '../../api/ApiController';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { InfinitySpin } from 'react-loader-spinner';
import { MdSearch, MdSearchOff } from 'react-icons/md';
import {
  AddIcon,
  EditIcon,
  CopyIcon,
  DeleteIcon,
  PrintIcon,
  SearchIcon,
  ExportIcon,
  VerticalLineIcon,
  BackToFirstIcon,
  BackIcon,
  ForwardIcon,
  ForwardToLastIcon,
  FavoriteIcon,
  PriceIcon,
  EditRowIcon,
} from '../../pages/svg/svgIcons';
import AuthContext from '../AuthContext';
import UtilContext from '../UtilContext';
import { LocaleContext } from '../LocaleContext';
import { defaultProductLogoSrc } from '../../constants/brandingDisplay';
import { buildCanonicalPrintFooterHtml, buildCanonicalPrintHeaderHtml } from '../../utils/printChrome';
import IntersectionObserverWrapper from '../toolbar/IntersectionObserverWrap';
import { OverflowMenuProvider } from '../toolbar/OverflowMenu';
import {
  scrollToFirst as scrollToFirstUtil,
  scrollUp as scrollUpUtil,
  scrollDown as scrollDownUtil,
  scrollToLast as scrollToLastUtil,
} from '../../utils/tabulatorScroll';
import { resetTabulatorMask } from '../../utils/tabulatorResetMask';
import TabulatorMaskDialog from '../../utils/tabulatorMaskDialog';
import {
  OTK_OPTION_CATALOG_REFRESH_EVENT,
  deleteOtkOptionCatalogSelectedRows,
  OTK_OPTION_CATALOG_STRATEGY,
  buildOtkOptionCatalogColumns,
  catalogUsesActionsFirstLayout,
  defaultVisibilityForCatalog,
  frozenLeadingFieldsForCatalog,
  getOtkOptionCatalogConfig,
  getOtkOptionCatalogMaskFieldLabel,
  isOtkOptionCatalogRefreshPayload,
  maskConfigurableFieldsForCatalog,
  mergeOtkOptionCatalogVisibility,
  otkOptionCatalogSearchFilter,
  requiredVisibilityFieldsForCatalog,
  validateFullCollectionResponse,
  OTK_OPTION_CATALOG_COLUMN_I18N_KEYS,
} from '../../utils/otkOptionCatalogList';
import { TABULATOR_AUDIT_MIN_ROW_HEIGHT } from '../../utils/tabulatorAuditColumns';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';

/**
 * Shared REMOTE_NON_PROGRESSIVE list for R8A OTK option catalogs.
 * Mount → exactly one full GET; local filter/sort/search; client print/xlsx.
 */
export default function OtkOptionCatalogList({ resource }) {
  const config = getOtkOptionCatalogConfig(resource);
  if (!config) {
    throw new Error(`Unknown OTK option catalog resource: ${resource}`);
  }

  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const {
    openFavoriteDialog,
    toast,
    setFastView,
    setUserMaskFilter,
    openChosenNotificationPreferences,
    getDateFormat,
    getUserInitials,
    getUserColor,
  } = useContext(UtilContext);
  const { t, locale } = useContext(LocaleContext);

  const ref = useRef(null);
  const overlayRef = useRef(null);
  const mountedFetchDoneRef = useRef(false);
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const listData = state[config.stateKey] || [];

  const [editIcon, setEditIcon] = useState(true);
  const [deleteIcon, setDeleteIcon] = useState(true);
  const [copyIcon, setCopyIcon] = useState(true);
  const [insertIcon] = useState(!user?.permissions?.includes('add otk-setup'));
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [visibleLoading, setVisibleLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchIconColor, setSearchIconColor] = useState('');
  const [maskVisible, setMaskVisible] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState({});
  const [columnsVisible, setColumnsVisible] = useState(() => defaultVisibilityForCatalog(config));

  const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFmt = `${dateFmt} HH:mm`;

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);

  const dispatchCollection = useCallback(
    (rows) => {
      dispatch({ type: config.loadAction, [config.loadPayloadKey]: rows });
    },
    [config.loadAction, config.loadPayloadKey, dispatch]
  );

  const fetchFullCollection = useCallback(
    ({ showLoading = false } = {}) => {
      if (showLoading) setVisibleLoading(true);
      return getRequest(config.getUrl)
        .then((response) => {
          const rows = validateFullCollectionResponse(response?.data);
          if (rows) dispatchCollection(rows);
          return rows;
        })
        .catch((reason) => {
          console.error(reason);
          toast?.current?.show({
            severity: 'warn',
            summary: t('Common.Error'),
            detail: t('Common.LoadFailed'),
            life: 3000,
          });
          return null;
        })
        .finally(() => {
          if (showLoading) setVisibleLoading(false);
        });
    },
    [config.getUrl, dispatchCollection, t, toast]
  );

  // Exactly one full GET on mount/open. Hide/unhide must not remount → no extra GET.
  useEffect(() => {
    if (mountedFetchDoneRef.current) return undefined;
    mountedFetchDoneRef.current = true;
    let cancelled = false;
    setVisibleLoading(true);
    getRequest(config.getUrl)
      .then((response) => {
        if (cancelled) return;
        const rows = validateFullCollectionResponse(response?.data);
        if (rows) dispatchCollection(rows);
      })
      .catch((reason) => {
        if (cancelled) return;
        console.error(reason);
        toast?.current?.show({
          severity: 'warn',
          summary: t('Common.Error'),
          detail: t('Common.LoadFailed'),
          life: 3000,
        });
      })
      .finally(() => {
        if (!cancelled) setVisibleLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only GET for REMOTE_NON_PROGRESSIVE
  }, []);

  // R8A-3 refresh contract: one full GET after confirmed mutation (emit from editors later).
  useEffect(() => {
    const onRefresh = (event) => {
      if (!isOtkOptionCatalogRefreshPayload(event?.detail, config.resource)) return;
      fetchFullCollection({ showLoading: false });
    };
    window.addEventListener(OTK_OPTION_CATALOG_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(OTK_OPTION_CATALOG_REFRESH_EVENT, onRefresh);
  }, [config.resource, fetchFullCollection]);

  const openAdd = useCallback(
    (entity, type) => {
      const props = {
        [config.entityProp]: entity,
        type,
      };
      if (entity != null || type === 'edit') {
        props.not_last_used = true;
      }
      const tabKey = type === 'edit' ? config.tabEditKey : config.tabAddKey;
      dispatch({
        type: 'create_new_tab',
        name: t(tabKey),
        translationKey: tabKey,
        legacyLabel: t(tabKey),
        page: config.addPage,
        props,
        mask: config.addMask,
      });
    },
    [config, dispatch, t]
  );

  const openAddRef = useRef(openAdd);
  openAddRef.current = openAdd;

  const columns = useMemo(() => {
    function EditRowFormatter(props) {
      const rowData = props.cell.getRow().getData();
      return (
        <div className="flex items-center justify-center w-full h-full">
          <button type="button" onClick={() => openAddRef.current(rowData, 'edit')}>
            <EditRowIcon />
          </button>
        </div>
      );
    }
    return buildOtkOptionCatalogColumns({
      config,
      t,
      locale,
      getLocale: () => localeRef.current,
      dateFormat: dateFmt,
      dateTimeFormat: dateTimeFmt,
      getUserColor,
      getUserInitials,
      editColumnFormatter: reactFormatter(<EditRowFormatter />),
    });
  }, [config, t, dateFmt, dateTimeFmt, getUserColor, getUserInitials]);

  useEffect(() => {
    const table = getTable();
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: OTK_OPTION_CATALOG_COLUMN_I18N_KEYS,
      placeholder: t('Common.NoData'),
      reformatRows: true,
    });
  }, [locale, t, getTable]);

  const maskFields = useMemo(
    () => [...maskConfigurableFieldsForCatalog(config)],
    [config]
  );

  const onSelectRow = useCallback(() => {
    setEditIcon(true);
    setCopyIcon(true);
    setDeleteIcon(true);
    const selected = getTable()?.getSelectedData?.() || [];
    if (selected.length > 0) {
      if (selected.length === 1) {
        if (user?.permissions?.includes('edit otk-setup')) {
          setCopyIcon(false);
          setEditIcon(false);
        }
      }
      if (user?.permissions?.includes('delete otk-setup')) setDeleteIcon(false);
    }
  }, [getTable, user?.permissions]);

  const acceptDelete = useCallback(() => {
    const rows = getTable()?.getSelectedData?.() || [];
    // N DELETEs (Promise.allSettled) → exactly one catalog GET; no typed refresh event.
    deleteOtkOptionCatalogSelectedRows({
      rows,
      deleteUrl: config.deleteUrl,
      deleteRequestFn: deleteRequest,
      fetchFullCollection,
      toast,
      t,
    });
  }, [config, fetchFullCollection, getTable, t, toast]);

  const confirmDelete = useCallback(() => {
    confirmDialog({
      message: t('ServiceTask.ConfirmDeleteRecord'),
      header: t('ServiceTask.ConfirmDeleteTitle'),
      icon: 'pi pi-info-circle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('Common.Yes'),
      rejectLabel: t('Common.No'),
      accept: acceptDelete,
      reject: () => {
        toast?.current?.show({
          severity: 'warn',
          summary: t('ServiceTask.Rejected'),
          detail: t('OtkOptionCatalog.Toast.RequestCancelled'),
          life: 3000,
        });
      },
    });
  }, [acceptDelete, t, toast]);

  const onRowMoved = useCallback(() => {
    if (!config.reorder) return;
    const table = getTable();
    if (!table) return;
    if (!user?.permissions?.includes('edit otk-setup')) {
      toast?.current?.show({
        severity: 'warn',
        summary: t('Common.Error'),
        life: 3000,
        detail: t('OtkOptionCatalog.Toast.NoPermission'),
      });
      return;
    }
    const orderedData = table.getData?.() || [];
    const formData = new FormData();
    orderedData.forEach((item, index) => {
      formData.append(`${config.reorderFormKey}[${index}]`, item.id);
    });
    postRequest(config.reorderUrl, formData, true)
      .then(() => {
        toast?.current?.show({
          severity: 'info',
          summary: t('OtkOptionCatalog.Toast.ReorderSavedSummary'),
          life: 2000,
          detail: t('OtkOptionCatalog.Toast.ReorderSavedDetail'),
        });
        // Exactly one full GET after confirmed successful reorder
        fetchFullCollection();
      })
      .catch((reason) => {
        console.error(reason);
        toast?.current?.show({
          severity: 'warn',
          summary: t('Common.Error'),
          life: 3000,
          detail: t('OtkOptionCatalog.Toast.ReorderFailed'),
        });
      });
  }, [config, fetchFullCollection, getTable, t, toast, user?.permissions]);

  const searchTable = useCallback(() => {
    const table = getTable();
    if (!table) return;
    setSearchIconColor('active');
    table.addFilter?.(otkOptionCatalogSearchFilter, {
      value: searchValue,
      locale,
      domainKind: config.domainKind,
    });
    if (searchValue === '') {
      setSearchIconColor('');
      table.removeFilter?.();
    }
    setVisibleSearch(false);
  }, [config.domainKind, getTable, locale, searchValue]);

  const clearSearch = useCallback(() => {
    setSearchIconColor('');
    setSearchValue('');
    getTable()?.clearFilter?.();
  }, [getTable]);

  const applyVisibility = useCallback(
    (visibilityMap) => {
      const table = getTable();
      if (!table) return;
      const required = requiredVisibilityFieldsForCatalog(config);
      for (const field of Object.keys(defaultVisibilityForCatalog(config))) {
        if (required.includes(field)) {
          table.showColumn?.(field);
          continue;
        }
        if (visibilityMap[field] === false) table.hideColumn?.(field);
        else table.showColumn?.(field);
      }
    },
    [config, getTable]
  );

  const setMask = useCallback(
    (field, value) => {
      if (requiredVisibilityFieldsForCatalog(config).includes(field)) return;
      const next = { ...columnsVisible, [field]: value };
      for (const req of requiredVisibilityFieldsForCatalog(config)) {
        next[req] = true;
      }
      applyVisibility(next);
      setColumnsVisible(next);
    },
    [applyVisibility, columnsVisible, config]
  );

  const maskSelectAll = useCallback(() => {
    const next = { ...defaultVisibilityForCatalog(config) };
    applyVisibility(next);
    setColumnsVisible(next);
  }, [applyVisibility, config]);

  const maskDeselectAll = useCallback(() => {
    const next = { ...defaultVisibilityForCatalog(config) };
    for (const field of maskConfigurableFieldsForCatalog(config)) {
      next[field] = false;
    }
    for (const field of requiredVisibilityFieldsForCatalog(config)) {
      next[field] = true;
    }
    applyVisibility(next);
    setColumnsVisible(next);
  }, [applyVisibility, config]);

  const closeMenu = useCallback(() => {
    OverflowMenuProvider.setVisible(null);
    setExpandedKeys({});
  }, []);

  const resetMask = useCallback(() => {
    resetTabulatorMask({
      page: config.pageKey,
      defaultColumnsVisible: defaultVisibilityForCatalog(config),
      ref,
      columns,
      setColumnsVisible,
    });
  }, [columns, config]);

  const handleClickOutside = useCallback((event) => {
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [handleClickOutside]);

  const itemRenderer = (item, options) => (
    <a
      className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]"
      onClick={options.onClick}
    >
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </a>
  );

  const items = useMemo(
    () => [
      {
        key: '0',
        label: t('Common.Actions'),
        icon: 'pi pi-sliders-v',
        template: itemRenderer,
        items: [
          {
            key: '0_1',
            label: t('Common.SetFilter'),
            icon: 'pi pi-filter',
            command: () => {
              setUserMaskFilter(config.pageKey, 'set_filters', null, getTable()?.getHeaderFilters?.(), null);
            },
            template: itemRenderer,
          },
          {
            key: '0_2',
            label: t('Common.ClearFilter'),
            icon: 'pi pi-filter-slash',
            command: () => {
              setUserMaskFilter(config.pageKey, 'set_filters', null, [], null);
              getTable()?.clearHeaderFilter?.();
            },
            template: itemRenderer,
          },
          {
            key: '0_3',
            label: t('Common.SetSorting'),
            icon: 'pi pi-sort-alt',
            command: () => {
              setUserMaskFilter(config.pageKey, 'set_sorters', null, null, null, getTable()?.getSorters?.());
              closeMenu();
            },
            template: itemRenderer,
          },
          {
            key: '0_4',
            label: t('Common.ClearSorting'),
            icon: 'pi pi-sort-alt-slash',
            command: () => {
              setUserMaskFilter(config.pageKey, 'set_sorters', null, null, null, []);
              getTable()?.setSort?.([]);
              closeMenu();
            },
            template: itemRenderer,
          },
          {
            key: '0_5',
            label: t('Common.SetMask'),
            icon: 'pi pi-server',
            command: () => {
              setMaskVisible(true);
              closeMenu();
            },
            template: itemRenderer,
          },
          {
            key: '0_5b',
            label: t('Common.ResetUserMask'),
            icon: 'pi pi-refresh',
            command: () => {
              resetMask();
              closeMenu();
            },
            template: itemRenderer,
          },
          {
            key: '0_6',
            label: t('Common.Notifications'),
            icon: 'pi pi-envelope',
            command: () => {
              openChosenNotificationPreferences(config.pageKey);
              closeMenu();
            },
            template: itemRenderer,
          },
        ],
      },
    ],
    [closeMenu, config.pageKey, getTable, openChosenNotificationPreferences, resetMask, setUserMaskFilter, t]
  );

  const tabulatorOptions = useMemo(() => {
    const opts = {
      layout: 'fitColumns',
      renderHorizontal: 'virtual',
      rowHeight: Math.max(30, TABULATOR_AUDIT_MIN_ROW_HEIGHT),
      movableColumns: true,
      pagination: false,
      filterMode: 'local',
      sortMode: 'local',
      printAsHtml: true,
      printHeader: () => buildCanonicalPrintHeaderHtml({
        branding: state?.branding,
        productFallbackSrc: defaultProductLogoSrc,
        companySettingsPrint: state?.company_settings_print,
        moduleSettings: state?.module_settings,
        title: t(config.printTitleKey),
        printDateLabel: t('Print.DateLabel'),
      }),
      printFooter: () => buildCanonicalPrintFooterHtml({ companySettingsPrint: state?.company_settings_print }),
      printRowRange: 'active',
    };
    if (config.reorder) {
      opts.movableRows = true;
      opts.initialSort = [{ column: 'defined_id', dir: 'asc' }];
    }
    return opts;
  }, [config.printTitleKey, config.reorder, state?.branding, state?.company_settings_print, state?.module_settings, t]);

  const events = useMemo(() => {
    const restoreLeadingOrderIfBroken = () => {
      const table = getTable();
      if (!table?.getColumns) return false;
      const order = table.getColumns(true).map((c) => c.getField?.() || c.getDefinition?.()?.field);
      const expectedLeading = frozenLeadingFieldsForCatalog(config);
      const leadingBroken = expectedLeading.some((field, index) => order[index] !== field);
      if (leadingBroken) {
        table.setColumns?.(columns);
        applyVisibility(columnsVisible);
        return true;
      }
      return false;
    };
    const ev = {
      rowSelectionChanged: onSelectRow,
      tableBuilt: () => {
        const table = getTable();
        if (!table) return;
        const mask = (state.user_mask_filters || []).filter((m) => m.page === config.pageKey);
        const safeParse = (v, fallback) => {
          if (v === null || v === undefined) return fallback;
          if (typeof v === 'string') {
            try {
              return JSON.parse(v);
            } catch {
              return fallback;
            }
          }
          return v;
        };
        if (mask.length > 0) {
          const attributes = safeParse(mask[0].attributes, []);
          const filters = safeParse(mask[0].filters, []);
          const sorters = safeParse(mask[0].sorters, []);
          const jsonColumns = safeParse(mask[0].columns, {});
          // Restore layout only when it matches this catalog's protected leading prefix.
          if (Array.isArray(attributes) && attributes.length > 1) {
            const expectedLeading = frozenLeadingFieldsForCatalog(config);
            const layoutFields = attributes.map((a) => a?.field).filter(Boolean);
            const leadingMatches = expectedLeading.every(
              (field, index) => layoutFields[index] === field
            );
            if (leadingMatches || (!catalogUsesActionsFirstLayout(config) && layoutFields[0] === 'id')) {
              table.setColumnLayout?.(attributes);
            }
          }
          if (Array.isArray(filters)) {
            filters.forEach((f) => table.setHeaderFilterValue?.(f.field, f.value));
          }
          if (Array.isArray(sorters) && sorters.length > 0) {
            table.setSort?.(sorters);
          }
          if (jsonColumns && typeof jsonColumns === 'object') {
            const merged = mergeOtkOptionCatalogVisibility(config, jsonColumns);
            setColumnsVisible(merged);
            applyVisibility(merged);
          }
        }
        restoreLeadingOrderIfBroken();
      },
      columnMoved: () => {
        if (restoreLeadingOrderIfBroken()) return;
        setUserMaskFilter(config.pageKey, 'column_move', null, null, getTable()?.getColumnLayout?.());
      },
      columnResized: () => {
        setUserMaskFilter(config.pageKey, 'column_move', null, null, getTable()?.getColumnLayout?.());
      },
    };
    if (config.reorder) {
      ev.rowMoved = onRowMoved;
    }
    return ev;
  }, [
    applyVisibility,
    columns,
    columnsVisible,
    config,
    getTable,
    onRowMoved,
    onSelectRow,
    setUserMaskFilter,
    state.user_mask_filters,
  ]);

  const doPrint = useCallback(() => {
    // Client print over complete in-memory / active-filtered set — no server export endpoint.
    getTable()?.print?.(false, true);
  }, [getTable]);

  const doXlsx = useCallback(() => {
    // Client XLSX over complete loaded dataset — no server export endpoint.
    getTable()?.download?.('xlsx', config.xlsxFilename, { sheetName: config.resource });
  }, [config.resource, config.xlsxFilename, getTable]);

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]" data-otk-strategy={OTK_OPTION_CATALOG_STRATEGY} data-otk-resource={config.resource} data-otk-mask={config.mask}>
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="add">
            <button type="button" className="disabled:opacity-25" disabled={insertIcon} onClick={() => openAdd(null, 'add')}>
              <AddIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit">
            <button
              type="button"
              className="disabled:opacity-25"
              disabled={editIcon}
              onClick={() => openAdd(getTable()?.getSelectedData?.()?.[0], 'edit')}
            >
              <EditIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="copy">
            <button
              type="button"
              className="disabled:opacity-25"
              disabled={copyIcon}
              onClick={() => openAdd(getTable()?.getSelectedData?.()?.[0], 'add')}
            >
              <CopyIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="delete">
            <button type="button" className="disabled:opacity-25" onClick={confirmDelete} disabled={deleteIcon}>
              <DeleteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button type="button" onClick={doPrint} title={t('Common.Print')}>
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button
              type="button"
              title={searchIconColor !== 'active' ? t('Common.Search') : t('ServiceTask.CancelSearch')}
              onClick={
                searchIconColor !== 'active'
                  ? () => setVisibleSearch(true)
                  : clearSearch
              }
            >
              {searchIconColor === '' ? (
                <MdSearch fill="#fff" strokeWidth="0" className="w-[28px] h-[28px]" aria-hidden="true" />
              ) : (
                <MdSearchOff fill="#fff" strokeWidth="0" className="w-[28px] h-[28px]" aria-hidden="true" />
              )}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
            <button type="button" onClick={doXlsx} title={t('Common.ExportToExcel')}>
              <ExportIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="tofirst">
            <button type="button" onClick={() => scrollToFirstUtil(getTable())}>
              <BackToFirstIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="toup">
            <button type="button" onClick={() => scrollUpUtil(getTable())}>
              <BackIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="todown">
            <button type="button" onClick={() => scrollDownUtil(getTable())}>
              <ForwardIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="tolast">
            <button type="button" onClick={() => scrollToLastUtil(getTable())}>
              <ForwardToLastIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button type="button" onClick={() => openFavoriteDialog(t(config.favoriteTitleKey), config.pageKey)}>
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast">
            <button type="button" onClick={() => setFastView(config.pageKey, null, null)}>
              <PriceIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <ReactTabulator
                key={`otk-option-catalog-${config.pageKey}`}
                data={listData}
                className="h-[calc(100vh_-_193px)]"
                onRef={(r) => {
                  ref.current = r.current;
                }}
                columns={columns}
                events={events}
                options={tabulatorOptions}
              />
            </div>
          </div>
        </div>
        <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
          <div className="flex flex-columns">
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              type="text"
              className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
            />
            <button
              type="button"
              onClick={searchTable}
              className="w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600"
            >
              <SearchIcon />
            </button>
          </div>
        </Dialog>
        <Dialog
          visible={visibleLoading}
          style={{ width: '25vw', height: '25vh', backgroundColor: 'transparent', boxShadow: 'none' }}
          className="mypanel flex flex-row"
          closable={false}
          draggable={false}
          showHeader={false}
        >
          <InfinitySpin width="208" color="#000" />
          <p className="text-2xl font-bold text-center w-52 text-black">{t('Common.LoadingData')}</p>
        </Dialog>
        <TabulatorMaskDialog
          visible={maskVisible}
          onHide={() => setMaskVisible(false)}
          fields={maskFields}
          checkedMap={columnsVisible}
          onToggleField={setMask}
          onSelectAll={maskSelectAll}
          onDeselectAll={maskDeselectAll}
          onSave={() => {
            setUserMaskFilter(config.pageKey, 'set_columns', columnsVisible, null, null);
            setMaskVisible(false);
          }}
          getFieldLabel={(field) => getOtkOptionCatalogMaskFieldLabel(field, t)}
          inputIdPrefix={`otk-opt-${config.resource}`}
        />
      </div>
    </>
  );
}
